import { useState } from 'react';
import { Form } from 'antd';

import { useApplicationContext } from '@/context/v1/Application/context';
import { useEvaluators } from '@/hooks/v1/useEvaluators';
import { useCreateEvaluator } from '@/hooks/v1/useEvaluatorMutations';

interface EvaluatorProps {
	fieldName?: string | string[];
}

const { useFormInstance, useWatch } = Form;

export function useEvaluator({ fieldName }: EvaluatorProps) {
	const form = useFormInstance();
	const { organization, company } = useApplicationContext();
	const { mutateAsync: createEvaluator } = useCreateEvaluator();
	const [evaluatorName, setEvaluatorName] = useState('');

	const field = Array.isArray(fieldName) ? fieldName[0] : null;

	const companyId = useWatch(field ? [field, 'company_id'] : 'company_id');
	const organizationId = useWatch(field ? [field, 'organization_id'] : 'organization_id');

	function handleEvaluatorSelect(id: string): void {
		form.setFieldValue(field ? [field, 'evaluator_id'] : ['evaluator_id'], id);
	}

	async function handleCreateEvaluator(): Promise<void> {
		const name = evaluatorName.trim();

		if (!name) return;

		const payload = {
			organization_id: organizationId,
			company_id: companyId,
			name
		};

		const result = await createEvaluator({ body: payload });

		const newId = result?.data?.id;

		if (newId) {
			handleEvaluatorSelect(newId);
		}

		setEvaluatorName('');
	}

	const { isLoading, data: evaluators } = useEvaluators({
		organization_id: organization.id!,
		company_id: company.id!
	});

	function handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
		setEvaluatorName(event.target.value);
	}

	const data =
		evaluators?.map((evaluator: { id: string; name: string }) => ({
			value: evaluator.id,
			label: evaluator.name
		})) || [];

	return {
		isLoading,
		data,
		handleEvaluatorSelect,
		handleCreateEvaluator,
		handleInputChange,
		evaluatorName
	};
}
